/**
 * Costs Tab Component
 *
 * Displays AWS cost breakdown with summary cards and charts.
 * Uses Cost Explorer API data filtered by Application=Compass tag.
 */

import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { InfoIcon } from 'lucide-react';
import { DashboardSection } from './DashboardSection';
import { TimeRangeSelector } from './TimeRangeSelector';
import { CostSummaryCards } from './CostSummaryCards';
import { CostTrendChart } from './CostTrendChart';
import { ServiceBreakdownChart } from './ServiceBreakdownChart';
import {
  TIME_RANGE_LABELS,
  type CostsResponse,
  type TimeRange,
} from '@/types/dashboard';

interface CostsTabProps {
  data: CostsResponse | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
}

export function CostsTab({
  data,
  isLoading,
  error,
  lastUpdated,
  timeRange,
  onTimeRangeChange,
}: CostsTabProps) {
  return (
    <div className="space-y-6">
      {/* Header with Time Range Selector */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-muted-foreground h-5 flex items-center">
          {lastUpdated ? (
            <>
              Last updated: {lastUpdated.toLocaleTimeString()}
              <span className="ml-2 text-xs">(refreshes every 15 min)</span>
            </>
          ) : (
            <Skeleton className="h-4 w-56" />
          )}
        </div>
        <TimeRangeSelector
          value={timeRange}
          onChange={onTimeRangeChange}
          disabled={isLoading}
        />
      </div>

      {/* Data Freshness Notice */}
      <Alert>
        <InfoIcon className="h-4 w-4" />
        <AlertTitle>Cost Data Freshness</AlertTitle>
        <AlertDescription>
          AWS Cost Explorer data is typically 24-48 hours behind. Costs shown
          are for resources tagged with <code>Application=Compass</code>.
        </AlertDescription>
      </Alert>

      {/* Error Display */}
      {error && (
        <div
          className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg"
          role="alert"
        >
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      <DashboardSection
        id="cost-summary"
        title="Cost Summary"
        description={`AWS costs for ${TIME_RANGE_LABELS[timeRange].toLowerCase()}`}
      >
        <CostSummaryCards
          data={data?.summary}
          isLoading={isLoading}
          timeRange={TIME_RANGE_LABELS[timeRange]}
        />
      </DashboardSection>

      {/* Daily Trend Chart */}
      <DashboardSection
        id="cost-trend"
        title="Daily Cost Trend"
        description="Cost breakdown by AWS service over time"
      >
        <CostTrendChart data={data?.daily} isLoading={isLoading} />
      </DashboardSection>

      {/* Service Breakdown */}
      <DashboardSection
        id="service-breakdown"
        title="Cost Distribution"
        description="Percentage breakdown by AWS service"
      >
        <div className="max-w-lg mx-auto">
          <ServiceBreakdownChart data={data?.byService} isLoading={isLoading} />
        </div>
      </DashboardSection>

      {/* Empty State Message */}
      {data?.message && (
        <Alert>
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>No Cost Data</AlertTitle>
          <AlertDescription>{data.message}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
